/**
 * Discount-code price preview
 * When a visitor arrives via /discount/CODE (Shopify sets the discount_code
 * cookie), builds a throwaway cart through the tokenless Storefront API with
 * the variants shown on the page + the code, and shows Shopify's real
 * discounted prices as crossed-out sale prices. The visitor's own cart is
 * never touched. Prices come from snippets/product-price.liquid (data-sdp-*).
 */
(function () {
	const cfg = window.sdpConfig || {};
	const CHUNK = 40; // lines per request (tokenless complexity limit 1000)
	const CACHE_MIN = 10;

	function getCookie(name) {
		const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
		return m ? decodeURIComponent(m[1]) : '';
	}

	const CODE = getCookie('discount_code').trim();
	if (!CODE && !cfg.automatic) return;

	const country = cfg.country || (window.Shopify && window.Shopify.country) || null;
	const cacheKey = 'sdp:' + CODE.toUpperCase() + ':' + country;

	/* ---------- money formatting from a Liquid sample of 1234.56 ---------- */
	const fmt = (function (sample) {
		sample = sample || '€1.234,56';
		const first = sample.indexOf('1');
		let last = -1;
		for (let i = sample.length - 1; i >= 0; i--) {
			if (/[0-9]/.test(sample[i])) { last = i; break; }
		}
		const prefix = sample.slice(0, first);
		const suffix = sample.slice(last + 1);
		const num = sample.slice(first, last + 1);
		const thousands = /[0-9]/.test(num[1]) ? '' : num[1];
		let decimals = 0;
		let decimal = '';
		if (/56$/.test(num) && !/[0-9]/.test(num[num.length - 3])) {
			decimals = 2;
			decimal = num[num.length - 3];
		}
		return function (cents) {
			const value = (cents / 100).toFixed(decimals);
			let [int, dec] = value.split('.');
			int = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
			return prefix + int + (decimals ? decimal + dec : '') + suffix;
		};
	})(cfg.sample);

	/* ---------- cache ---------- */
	function readCache() {
		try {
			const raw = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
			if (raw && Date.now() - raw.t < CACHE_MIN * 60000) return raw.p;
		} catch (e) {}
		return {};
	}
	function writeCache(prices) {
		try { sessionStorage.setItem(cacheKey, JSON.stringify({ t: Date.now(), p: prices })); } catch (e) {}
	}
	const prices = readCache(); // variantId -> discounted cents per unit, or 0 = no discount

	/* ---------- Storefront API ---------- */
	const QUERY = `mutation SdpPreview($lines: [CartLineInput!]!, $codes: [String!], $country: CountryCode, $language: LanguageCode) @inContext(country: $country, language: $language) {
		cartCreate(input: { lines: $lines, discountCodes: $codes, buyerIdentity: { countryCode: $country } }) {
			cart {
				discountAllocations {
					__typename
					discountApplication { allocationMethod targetSelection value { __typename ... on PricingPercentageValue { percentage } } }
				}
				lines(first: ${CHUNK}) {
					nodes {
						quantity
						merchandise { ... on ProductVariant { id } }
						cost { subtotalAmount { amount } }
						discountAllocations {
							__typename
							discountedAmount { amount }
							discountApplication { allocationMethod targetSelection value { __typename } }
						}
					}
				}
			}
			userErrors { message }
		}
	}`;

	// Fallback without discountApplication in case the field is unavailable
	const QUERY_SIMPLE = QUERY.replace(/\n\s*discountApplication \{[^\n]*\}(?=\n)/g, '');
	let useSimple = false;

	async function fetchChunk(ids) {
		const body = {
			query: useSimple ? QUERY_SIMPLE : QUERY,
			variables: {
				lines: ids.map((id) => ({ merchandiseId: 'gid://shopify/ProductVariant/' + id, quantity: 1 })),
				codes: CODE ? [CODE] : [],
				country: country,
				language: cfg.language || null
			}
		};
		const res = await fetch('/api/' + (cfg.apiVersion || '2026-01') + '/graphql.json', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(body)
		});
		const json = await res.json();
		if (json.errors && !useSimple) {
			useSimple = true;
			return fetchChunk(ids);
		}
		const cart = json && json.data && json.data.cartCreate && json.data.cartCreate.cart;
		const lines = cart ? cart.lines.nodes : [];
		// Order-level discounts (e.g. "10% off entire order") are reported on the cart,
		// not on lines – a percentage one equals the same % off every product
		let orderPct = 0;
		((cart && cart.discountAllocations) || []).forEach((a) => {
			if (a.__typename === 'CartAutomaticDiscountAllocation' && !cfg.automatic) return;
			const v = a.discountApplication && a.discountApplication.value;
			if (v && v.__typename === 'PricingPercentageValue') orderPct = Math.max(orderPct, v.percentage);
		});
		const found = {};
		lines.forEach((line) => {
			const id = line.merchandise && line.merchandise.id ? line.merchandise.id.split('/').pop() : null;
			if (!id) return;
			const subtotal = Math.round(parseFloat(line.cost.subtotalAmount.amount) * 100);
			let off = 0;
			(line.discountAllocations || []).forEach((a) => {
				if (a.__typename === 'CartAutomaticDiscountAllocation' && !cfg.automatic) return;
				const app = a.discountApplication || {};
				// Fixed amount off the whole order is spread across lines – not a product price
				if (app.allocationMethod === 'ACROSS' && app.value && app.value.__typename === 'MoneyV2') return;
				off += Math.round(parseFloat(a.discountedAmount.amount) * 100);
			});
			if (orderPct) off += Math.round((subtotal - off) * orderPct / 100);
			found[id] = off > 0 ? Math.max(0, Math.round((subtotal - off) / line.quantity)) : 0;
		});
		ids.forEach((id) => { prices[id] = found[id] !== undefined ? found[id] : 0; });
	}

	/* ---------- render ---------- */
	function apply(el) {
		const id = el.dataset.sdpVariant;
		const cents = prices[id];
		if (!cents) { el.dataset.sdpDone = '1'; return; }
		const current = parseInt(el.dataset.sdpPrice, 10);
		el.dataset.sdpDone = '1';
		if (!(cents < current)) return;

		const ins = el.querySelector(':scope > ins');
		if (!ins) return;
		el.querySelectorAll(':scope > del').forEach((d) => d.remove());

		const del = document.createElement('del');
		const oldSpan = document.createElement('span');
		oldSpan.className = 'amount';
		oldSpan.textContent = el.dataset.sdpOld;
		del.appendChild(oldSpan);
		el.insertBefore(del, ins);

		const amount = document.createElement('span');
		amount.className = 'amount discounted';
		const safe = fmt(cents).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
		amount.innerHTML = (el.dataset.sdpTpl || '%PRICE%').replace('%PRICE%', safe);
		ins.replaceChildren(amount);
		el.classList.add('price--code-discount');
		updateBadge(el, cents);
	}

	/* Sale badge – same markup/text as the theme's compare-at sale badge */
	function updateBadge(el, cents) {
		if (el.dataset.sdpAvailable !== 'true') return;
		const card = el.closest('product-card, .product-card');
		const pdp = el.closest('.product-price-container') && el.closest('.product-information--inner');
		let holder = null;
		if (card) holder = card.querySelector('.product-card--badges');
		else if (pdp) holder = pdp.querySelector(':scope > .badges'); // "badges" custom liquid block on product page
		if (!holder) holder = el.querySelector(':scope > .badges');
		if (!holder) return;

		const old = parseInt(el.dataset.sdpOldCents, 10);
		let text = cfg.badgeSale || 'Sale';
		if (cfg.badgeType === 'save_amount' || cfg.badgeType === 'save_percentage') {
			const saved = cfg.badgeType === 'save_amount'
				? fmt(old - cents)
				: Math.round((old - cents) * 100 / old) + '%';
			text = (cfg.badgeSave || 'Save %AMOUNT%').replace('%AMOUNT%', saved);
		}

		let badge = holder.querySelector('.badge.onsale');
		if (!badge) {
			badge = document.createElement('span');
			badge.className = 'badge onsale';
			// cards: sold out, sale, ... – product page block: sale first
			const soldOut = holder.querySelector('.badge.out-of-stock');
			if (soldOut && card) soldOut.after(badge);
			else holder.prepend(badge);
		}
		badge.innerHTML = text;
		badge.classList.add('badge--code-discount');
	}

	let queued = false;
	async function run() {
		queued = false;
		const els = [...document.querySelectorAll('.price[data-sdp-variant]:not([data-sdp-done])')]
			.filter((el) => el.dataset.sdpVariant);
		// Sold-out variants can't go into a cart – they keep their normal price
		els.filter((el) => el.dataset.sdpAvailable !== 'true').forEach((el) => { el.dataset.sdpDone = '1'; });
		if (!els.length) return;
		const live = els.filter((el) => el.dataset.sdpAvailable === 'true');
		const missing = [...new Set(live.map((el) => el.dataset.sdpVariant))].filter((id) => !(id in prices));
		try {
			for (let i = 0; i < missing.length; i += CHUNK) {
				await fetchChunk(missing.slice(i, i + CHUNK));
			}
			if (missing.length) writeCache(prices);
		} catch (e) {
			return; // fail silently – normal prices stay
		}
		live.forEach(apply);
	}

	function queue() {
		if (queued) return;
		queued = true;
		setTimeout(run, 50);
	}

	function init() {
		queue();
		// Variant changes, filters, quick view and recommendations re-render prices
		new MutationObserver((mutations) => {
			for (const m of mutations) {
				for (const n of m.addedNodes) {
					if (n.nodeType === 1 && (n.matches('.price[data-sdp-variant]') || n.querySelector('.price[data-sdp-variant]'))) {
						queue();
						return;
					}
				}
			}
		}).observe(document.body, { childList: true, subtree: true });
	}

	if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
	else init();
})();